import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Download, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getTransactions } from "@/lib/wallets.functions";
import { formatMoney } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/app/transactions")({
  component: TransactionsPage,
  head: () =>
    privatePageMetadata(
      "/app/transactions",
      "Transactions | BeHot.Now",
      "Your BeHot.Now transaction history.",
    ),
});

function TransactionsPage() {
  const { t } = useT();
  const WALLET_LABELS: Record<string, string> = {
    topup: t("dashboard.transactions.walletTopup"),
    earn: t("dashboard.transactions.walletEarn"),
    referral: t("dashboard.transactions.walletReferral"),
  };
  const fetchTx = useServerFn(getTransactions);
  const [query, setQuery] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["transactions"],
    queryFn: () => fetchTx(),
  });

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((r) =>
      [r.description, r.kind, r.wallet ?? "", r.reference ?? "", r.status]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [data, query]);

  function exportCsv() {
    const header = [
      t("dashboard.transactions.csvDate"),
      t("dashboard.transactions.csvType"),
      t("dashboard.transactions.csvWallet"),
      t("dashboard.transactions.csvDescription"),
      t("dashboard.transactions.csvAmount"),
      t("dashboard.transactions.csvStatus"),
      t("dashboard.transactions.csvReference"),
    ];
    const lines = rows.map((r) =>
      [
        new Date(r.date).toISOString(),
        r.kind,
        r.wallet ? WALLET_LABELS[r.wallet] ?? r.wallet : "",
        r.description,
        (r.amountCents / 100).toFixed(2),
        r.status,
        r.reference ?? "",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(","),
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `behot-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <h2 className="font-display text-xl font-bold">{t("dashboard.transactions.heading")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("dashboard.transactions.aedNotice")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input
            className="w-48"
            placeholder={t("dashboard.transactions.searchPlaceholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button variant="outline" size="sm" onClick={exportCsv}>
            <Download className="size-4" /> {t("dashboard.transactions.csv")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="size-4" /> {t("dashboard.transactions.pdf")}
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3">{t("dashboard.transactions.colDate")}</th>
              <th className="px-4 py-3">{t("dashboard.transactions.colDescription")}</th>
              <th className="px-4 py-3">{t("dashboard.transactions.colWallet")}</th>
              <th className="px-4 py-3">{t("dashboard.transactions.colStatus")}</th>
              <th className="px-4 py-3 text-right">{t("dashboard.transactions.colAmount")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td className="px-4 py-6 text-muted-foreground" colSpan={5}>
                  {t("dashboard.transactions.loading")}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-muted-foreground" colSpan={5}>
                  {t("dashboard.transactions.empty")}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-3 whitespace-nowrap">
                  {new Date(r.date).toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  {r.description}
                  {r.reference && (
                    <span className="block text-xs text-muted-foreground">{r.reference}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {r.wallet ? WALLET_LABELS[r.wallet] ?? r.wallet : t("dashboard.transactions.walletCard")}
                </td>
                <td className="px-4 py-3 capitalize">{r.status}</td>
                <td
                  className={`px-4 py-3 text-right font-semibold ${
                    r.amountCents >= 0 ? "text-emerald-600" : ""
                  }`}
                >
                  {r.amountCents >= 0 ? "+" : "−"}
                  {formatMoney(Math.abs(r.amountCents))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
